import { Box, Typography, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress, Button } from '@mui/material';
import { CloudUpload, Warning, Crop, Edit, PhotoLibrary, Download, Delete } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useState } from 'react';
import type { Photo } from '../../../types/photo';
import type { SlotUploadState } from './PhotoSlotComponents';
import { downloadSinglePhoto } from '../../../services/photoService';

interface Props {
  photos: Photo[];
  findPhoto: (stage: string, position: string) => Photo | undefined;
  uploadStates?: Record<string, SlotUploadState>;
  onClickUpload: (stage: string, position: string) => void;
  onDrop: (e: React.DragEvent, stage: string, position: string) => void;
  onDelete: (photoKey: string) => Promise<void>;
  onRecrop: (photo: Photo) => void;
  onEditCropped: (photo: Photo) => void;
  fileInputRefs: React.MutableRefObject<Record<string, HTMLInputElement | null>>;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>, stage: string, position: string) => void;
  readOnly?: boolean;
}

/**
 * Thumbnail frame = the Direct Shear REPORT's photo cell: square (1:1) with
 * the whole crop shown inside it (object-fit contain), never re-cropped or
 * rotated — so what the Photos page previews is exactly what the PDF prints
 * (BUG-2026-0172 rework, Shawn Hegger 2026-08-12: "the preview should match
 * how it will look in the report"; was a 7:8 cover-crop). Legacy 7:8 crops
 * letterbox inside the square here just as they do on the report page.
 */
const DS_REPORT_CELL_ASPECT = '1 / 1';

const STAGES = [
  { key: 'pre-test', label: 'Pre-test' },
  { key: '1', label: 'Stage 1' },
  { key: '2', label: 'Stage 2' },
  { key: '3', label: 'Stage 3' },
  { key: '4', label: 'Stage 4' },
  { key: '5', label: 'Stage 5' },
];

export const PhotoGridMedium = ({ findPhoto, uploadStates, onClickUpload, onDrop, onDelete, onRecrop, onEditCropped, fileInputRefs, onInputChange, readOnly = false }: Props) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 1.5 }}>
      {STAGES.map((stage) => {
        // Bottom first, Top second — matches the photo-taking order and batch slot fill order
        const slots = stage.key === 'pre-test'
          ? [
              { photo: findPhoto('pre-test', 'bottom'), label: 'Before Test (Bottom)', stg: 'pre-test', pos: 'bottom' },
              { photo: findPhoto('pre-test', 'top'), label: 'Before Test (Top)', stg: 'pre-test', pos: 'top' },
            ]
          : [
              { photo: findPhoto(stage.key, 'bottom'), label: 'After Test (Bottom)', stg: stage.key, pos: 'bottom' },
              { photo: findPhoto(stage.key, 'top'), label: 'After Test (Top)', stg: stage.key, pos: 'top' },
            ];

        return (
          <Box key={stage.key}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontSize: '0.85rem' }}>
              {stage.label}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              {slots.map((s) => (
                <MediumSlot
                  key={`${s.stg}_${s.pos}`}
                  photo={s.photo} label={s.label} stage={s.stg} position={s.pos} theme={theme}
                  uploadState={uploadStates?.[`${s.stg}_${s.pos}`]}
                  onClickUpload={() => onClickUpload(s.stg, s.pos)}
                  onDrop={(e) => onDrop(e, s.stg, s.pos)}
                  onDelete={onDelete} onRecrop={onRecrop} onEditCropped={onEditCropped}
                  fileInputRefs={fileInputRefs} onInputChange={onInputChange}
                  readOnly={readOnly}
                />
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

function MediumSlot({ photo, label, stage, position, theme, uploadState, onClickUpload, onDrop, onDelete, onRecrop, onEditCropped, fileInputRefs, onInputChange, readOnly = false }: {
  photo: Photo | undefined; label: string; stage: string; position: string; theme: any;
  uploadState?: SlotUploadState;
  onClickUpload: () => void; onDrop: (e: React.DragEvent) => void;
  onDelete: (photoKey: string) => Promise<void>;
  onRecrop: (photo: Photo) => void;
  onEditCropped: (photo: Photo) => void;
  fileInputRefs: React.MutableRefObject<Record<string, HTMLInputElement | null>>;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>, stage: string, position: string) => void;
  readOnly?: boolean;
}) {
  const [dragOver, setDragOver] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const slotId = `${stage}_${position}`;

  if (photo) {
    return (
      <Box>
        <Box sx={{ position: 'relative', cursor: readOnly ? 'default' : 'pointer' }}
          onClick={readOnly ? undefined : (e) => setMenuAnchor(e.currentTarget)}>
          <Box component="img" src={photo.thumbnailUrl || photo.imageUrl} alt={label} loading="lazy"
            onError={(e: any) => { if (photo.thumbnailUrl && e.target.src !== photo.imageUrl) e.target.src = photo.imageUrl; }}
            sx={{
              width: '100%', aspectRatio: DS_REPORT_CELL_ASPECT, objectFit: 'contain', display: 'block',
              bgcolor: theme.palette.background.paper,
              borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            }} />
          {photo.ProcessingStatus === 'failed' && (
            // BUG-2026-0205: the photo is kept exactly as uploaded when the
            // markers can't be found — already-cropped uploads need no action.
            <Tooltip title="Not auto-cropped — kept exactly as uploaded (scale markers not found). Already-cropped photos need no action; otherwise use Re-crop.">
              <Warning sx={{ position: 'absolute', top: 2, left: 2, fontSize: 16, color: 'warning.main' }} />
            </Tooltip>
          )}
        </Box>

        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => { setMenuAnchor(null); setConfirmDelete(false); }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          slotProps={{ paper: { sx: { minWidth: 160 } } }}>
          <MenuItem dense onClick={() => { setMenuAnchor(null); onEditCropped(photo); }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
            <ListItemText>Edit cropped</ListItemText>
          </MenuItem>
          <MenuItem dense onClick={() => { setMenuAnchor(null); onRecrop(photo); }}>
            <ListItemIcon><Crop fontSize="small" /></ListItemIcon>
            <ListItemText>Re-crop from original</ListItemText>
          </MenuItem>
          <MenuItem dense onClick={() => { setMenuAnchor(null); window.open(photo.originalImageUrl || photo.imageUrl, '_blank'); }}>
            <ListItemIcon><PhotoLibrary fontSize="small" /></ListItemIcon>
            <ListItemText>View original</ListItemText>
          </MenuItem>
          <MenuItem dense onClick={() => { setMenuAnchor(null); downloadSinglePhoto(photo.imageUrl, photo.FileName); }}>
            <ListItemIcon><Download fontSize="small" /></ListItemIcon>
            <ListItemText>Download</ListItemText>
          </MenuItem>
          {confirmDelete ? (
            <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '0.8rem', color: 'error.main', fontWeight: 600, mr: 0.5 }}>Delete?</Typography>
              <Button size="small" variant="contained" color="error"
                sx={{ minWidth: 0, px: 1.5, py: 0.2, fontSize: '0.72rem', textTransform: 'none' }}
                onClick={() => { setMenuAnchor(null); setConfirmDelete(false); onDelete(photo.PhotoKey); }}>
                Yes
              </Button>
              <Button size="small" variant="outlined"
                sx={{ minWidth: 0, px: 1.5, py: 0.2, fontSize: '0.72rem', textTransform: 'none' }}
                onClick={() => setConfirmDelete(false)}>
                No
              </Button>
            </Box>
          ) : (
            <MenuItem dense onClick={() => setConfirmDelete(true)} sx={{ color: 'error.main' }}>
              <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          )}
        </Menu>

        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.3, fontSize: '0.65rem', color: 'text.secondary' }}>
          {label}
        </Typography>
      </Box>
    );
  }

  const isUploading = uploadState?.status === 'uploading';

  if (readOnly) {
    return (
      <Box>
        <Box sx={{
          aspectRatio: DS_REPORT_CELL_ASPECT, borderRadius: 1,
          border: `1.5px dashed ${alpha(theme.palette.text.primary, 0.1)}`,
          bgcolor: alpha(theme.palette.text.primary, 0.02),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>Empty</Typography>
        </Box>
        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.3, fontSize: '0.65rem', color: 'text.secondary' }}>
          {label}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { setDragOver(false); onDrop(e); }}
        onClick={() => !isUploading && onClickUpload()}
        sx={{
          aspectRatio: DS_REPORT_CELL_ASPECT, borderRadius: 1, cursor: isUploading ? 'default' : 'pointer',
          border: `1.5px dashed ${isUploading ? theme.palette.primary.main : dragOver ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.15)}`,
          bgcolor: isUploading ? alpha(theme.palette.primary.main, 0.06) : dragOver ? alpha(theme.palette.primary.main, 0.04) : alpha(theme.palette.text.primary, 0.02),
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s', gap: 0.3,
          '&:hover': !isUploading ? { borderColor: theme.palette.primary.main } : {},
        }}>
        {isUploading ? (
          <>
            <CircularProgress size={20} thickness={5} />
            <Typography variant="caption" color="primary" sx={{ fontSize: '0.55rem', fontWeight: 600 }}>
              {uploadState.step === 'uploading' ? 'Uploading...' : uploadState.step === 'processing' ? 'Processing...' : 'Saving...'}
            </Typography>
          </>
        ) : (
          <>
            <CloudUpload sx={{ fontSize: 20, color: alpha(theme.palette.text.primary, 0.25) }} />
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>Click to Upload</Typography>
          </>
        )}
      </Box>
      <input type="file" accept="image/*" hidden
        ref={(el) => { fileInputRefs.current[slotId] = el; }}
        onChange={(e) => onInputChange(e, stage, position)} />
      <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.3, fontSize: '0.65rem', color: 'text.secondary' }}>
        {label}
      </Typography>
    </Box>
  );
}
